<template>
  <component
    :is="type === 'display' ? 'div' : 'button'"
    :type="type === 'display' ? undefined : 'button'"
    :aria-pressed="type === 'filter' ? active : undefined"
    class="relative block border-b-2 bg-neutral-50 px-4 text-left transition-colors duration-150 ease-out-quart md:shrink-0 md:px-8"
    :class="[
      fixedWidth ? 'w-full md:w-[16rem]' : 'w-full',
      type === 'filter' && active ? 'border-primary-400' : 'border-transparent',
      type !== 'display' ? 'cursor-pointer' : '',
      compact ? 'min-h-[5.25rem] py-4' : 'min-h-[7rem] py-6'
    ]"
    @click="onClick"
  >
    <div class="flex items-end" :class="[compact ? 'gap-1' : 'gap-2']">
      <h1
        class="font-bold transition-colors duration-150 ease-out-quart"
        :class="[
          type === 'filter' ? (active ? 'text-neutral-700' : 'text-neutral-500') : 'text-neutral-700',
          compact ? 'text-2xl' : 'text-4xl'
        ]"
      >
        <count-up :start-val="startValue" :end-val="value" :duration="2.5" :options="{ separator: '.' }" />
      </h1>

      <span
        v-if="total"
        class="text-xl font-normal"
        :class="[type === 'filter' ? (active ? 'text-neutral-700' : 'text-neutral-500') : 'text-neutral-700']"
      >
        / {{ total }}
      </span>
    </div>

    <h2
      class="font-normal text-neutral-500 transition-colors duration-150 ease-out-quart"
      :class="[
        type === 'filter' ? (active ? 'text-neutral-500' : 'text-neutral-500') : 'text-neutral-500',
        compact ? 'text-sm' : 'text-md'
      ]"
    >
      {{ label() }}
    </h2>

    <font-awesome-icon
      v-if="type === 'link'"
      :icon="['far', 'turn-down-right']"
      size="1x"
      class="fixed bottom-[-2rem] right-0 rounded-full bg-white p-4 text-neutral-500 transition-colors duration-150 ease-out-quart hover:text-neutral-600"
    />
  </component>
</template>

<script lang="ts" setup>
import CountUp from 'vue-countup-v3';
import { ref, watch } from 'vue';

interface Props {
  id: string;
  active: boolean;
  value: number;
  total?: number;
  type: 'filter' | 'link' | 'display';
  compact?: boolean;
  label: () => string;
  fixedWidth?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  active: false,
  total: undefined,
  type: 'filter',
  compact: false,
  fixedWidth: true
});

const emit = defineEmits(['click']);

const isFirstValueUpdate = ref(true);
const startValue = ref(0);

watch(
  () => props.value,
  (_, oldVal) => {
    if (isFirstValueUpdate.value) {
      isFirstValueUpdate.value = false;
      return; // skip initial value update
    }

    startValue.value = oldVal; // make subsequent values animate from previous value
  }
);

function onClick() {
  if (props.type !== 'display') {
    emit('click', props.id);
  }
}
</script>
